import React from "react";
import getOptions from "../../../../utils/getOptions";
import { Space, Tag } from "antd";
import MenuBoton from "./MenuBoton";
import days from "dayjs";
// import useAuth from '../../../../hooks/useAuth';
import localizedFormat from "dayjs/plugin/localizedFormat";
import { EnvironmentOutlined } from "@ant-design/icons";
import { categories, CATEGORY_COLORS } from "./eventCategories";
days.extend(localizedFormat);
days.locale("es");

const TYPE_LABELS = Object.fromEntries(
  categories.map((c) => [c.key, c.label])
);
const RISK_LABELS = Object.fromEntries(
  (categories.find((c) => c.key === "riesgo")?.subItems || []).map((si) => [
    si.key,
    si.label,
  ])
);
RISK_LABELS.na = "No aplica";

// Eventos creados antes de habilitar la selección múltiple guardaron
// "type"/"risk" como string simple en vez de array; se soportan ambos formatos.
const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === "") return [];
  return [value];
};

export default function TablaEventos(items, fetchData) {
  //const { session } = useAuth();
  const estadoFiltros = {
    InProgress: "Ejecutando",
    FalseAlarm: "Falsa alarma",
    Closed: "Cerrado",
    Open: "Abierto",
  };

  const formatDate = (date) => {
    return date ? days(date).format("DD/MMM/YYYY") : "Sin fecha";
  };

  const formatDateHour = (date) => {
    return date ? days(date).format("hh:mm A") : "Sin HORA";
  };

  const FormatearMoneda = ({ valor }) => {
    const valorFormateado = new Intl.NumberFormat("es-CO", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(valor);

    return <span>{valorFormateado}</span>;
  };

  return [
    {
      title: "Evento Id",
      dataIndex: "id",
      key: "id",
      aling: "center",
      fixed: "left",
      filterSearch: true,
      filters: getOptions(items, "id"),
      onFilter: (v, record) => record.id.indexOf(v) === 0,
      render: (text, record) => <Space>{text}</Space>,
    },

    {
      title: "Ubicación",
      dataIndex: "location",
      key: "nameLocation",
      align: "center",
      filterSearch: true,
      filters: getOptions(items, "location", "name"),
      onFilter: (v, record) =>
        record.location &&
        record.location.name &&
        record.location.name.indexOf(v) === 0,
      render: (text) => (
        <Space direction="vertical" size={2}>
          <Tag icon={<EnvironmentOutlined></EnvironmentOutlined>}>
            {text && text.name}
          </Tag>
          <Tag>Zona: {text && text.zone}</Tag>
        </Space>
      ),
    },
    {
      title: "Fecha de creación",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (text) => (
        <Space direction="vertical" size={2}>
          <>{formatDate(text)}</>
          <>{formatDateHour(text)}</>
        </Space>
      ),
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      aling: "center",
      fixed: "left",
      filterSearch: true,
      filters: [...new Set(items.flatMap((i) => toArray(i.type)))]
        .sort()
        .map((t) => ({ text: TYPE_LABELS[t] || t, value: t })),
      onFilter: (v, record) => toArray(record.type).includes(v),
      render: (type) => (
        <Space size={4} wrap>
          {toArray(type).map((t) => (
            <Tag key={t} color={CATEGORY_COLORS[t]}>
              {TYPE_LABELS[t] || t}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Riesgo",
      dataIndex: "risk",
      key: "risk",
      aling: "center",
      fixed: "left",
      filterSearch: true,
      filters: [...new Set(items.flatMap((i) => toArray(i.risk)))]
        .sort()
        .map((r) => ({ text: RISK_LABELS[r] || r, value: r })),
      onFilter: (v, record) => toArray(record.risk).includes(v),
      render: (risk) => (
        <Space size={4} wrap>
          {toArray(risk)
            .filter((r) => r !== "na")
            .map((r) => (
              <Tag key={r}>{RISK_LABELS[r] || r}</Tag>
            ))}
        </Space>
      ),
    },

    // {
    //   title: "Causa",
    //   dataIndex: "cause",
    //   key: "cause",
    //   aling: "center",
    //   fixed: "left",
    //   filterSearch: true,
    //   filters: getOptions(items, "cause"),
    //   onFilter: (v, record) => record.risk.indexOf(v) === 0,
    //   render: (text, record) => <Space>{text}</Space>,
    // },

    {
      title: "Causas",
      dataIndex: "causes",
      key: "causes",
      filterSearch: true,
      width: 200,
      align: "center",
      filters: [...new Set(items.flatMap((i) => i.causes || []))]
        .sort()
        .map((c) => ({ text: c.replace(/-/g, " "), value: c })),
      onFilter: (value, record) => (record.causes || []).includes(value),
      render: (causes) => (
        <Space direction="vertical" size={2}>
          {(causes || []).map((cause, i) => (
            <>{cause.replace(/-/g, " ")}</>
          ))}
        </Space>
      ),
    },

    {
      title: "Asunto",
      width: 300,
      dataIndex: "title",
      key: "title",
      filterSearch: true,
      filters: getOptions(items, "title"),
      onFilter: (v, record) => record.title.indexOf(v) === 0,
    },
    {
      title: "Costo",
      width: 100,
      dataIndex: "cost",
      key: "cost",
      render: (text) => (
        <Space>
          <FormatearMoneda valor={text.total} />
        </Space>
      ),
    },

    {
      title: "Estado",
      dataIndex: "state",
      key: "state",
      width: 100,
      fixed: "right",
      filters: Object.keys(estadoFiltros).map((key) => ({
        text: estadoFiltros[key],
        value: key,
      })),
      onFilter: (v, record) => record.state.indexOf(v) === 0,
      render: (text, record) => (
        <Space>
          {text === "InProgress" && (
            <Tag key={text} color="blue">
              {estadoFiltros[text]}
            </Tag>
          )}
          {text === "FalseAlarm" && (
            <Tag key={text} color="red">
              {estadoFiltros[text]}
            </Tag>
          )}
          {text === "Closed" && (
            <Tag key={text} color="green">
              {estadoFiltros[text]}
            </Tag>
          )}
          {text === "Open" && (
            <Tag key={text} color="yellow">
              {estadoFiltros[text]}
            </Tag>
          )}
        </Space>
      ),
    },

    {
      title: "Acciones",
      dataIndex: "url",
      key: "url",
      width: 100,
      fixed: "right",
      filterSearch: true,
      aling: "center",
      render: (text, record) => (
        <MenuBoton key={record.id} item={record} fetchData={fetchData} />
      ),
    },
  ];
}
